import React, { useState, useRef, useEffect } from 'react';
import html2canvas from 'html2canvas';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
// Tabs removed - using custom Button grid instead
import { 
  Terminal, 
  Play, 
  Server, 
  Globe, 
  Loader2, 
  Trash2,
  Copy,
  Check,
  Image,
  Download,
  BarChart3,
  X,
  TrendingUp,
  TrendingDown,
  Search,
  Info,
  Shield,
  ShieldAlert,
  ShieldCheck,
  FileJson,
  FileSpreadsheet,
  ExternalLink,
  Wifi,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { IS_PRERENDER } from '@/lib/prerender';

const TOOLS_API_URL = 'https://api.threat.live/api/tools-api';
const TOOLS_API_KEY = 'RTBH_SUPER_SECRET_KEY_8737';
const TURNSTILE_SITEKEY = '0x4AAAAAACLpdpidtZFYh3Ns';
const IP_INFO_API = 'https://api.threat.live/api/ip-info.php';

const ToolsPage = () => {
  const { language } = useLanguage();
  const [nodes, setNodes] = useState([]);
  const [selectedNode, setSelectedNode] = useState('all');
  const [target, setTarget] = useState('');
  const [activeTool, setActiveTool] = useState('ipinfo');
  const [isRunning, setIsRunning] = useState(false);
  const [output, setOutput] = useState([]); // For single node: [{ text, type }]
  const [nodeResults, setNodeResults] = useState([]); // For all nodes: [{ nodeId, nodeLocation, status, executionTime, lines: [] }]
  const [copied, setCopied] = useState(false);
  const [copiedImage, setCopiedImage] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  // IP Info states
  const [ipInput, setIpInput] = useState('');
  const [ipResults, setIpResults] = useState([]);
  const [ipError, setIpError] = useState('');
  const [copiedAll, setCopiedAll] = useState(null);
  const [ipTurnstileToken, setIpTurnstileToken] = useState(null);
  // DNS Recursive Check states
  const [dnsInput, setDnsInput] = useState('');
  const [dnsResults, setDnsResults] = useState(null); // Single node result
  const [dnsNodeResults, setDnsNodeResults] = useState([]); // All nodes results
  const [dnsError, setDnsError] = useState('');
  const [dnsLoading, setDnsLoading] = useState(false);
  const terminalRef = useRef(null);
  const turnstileRef = useRef(null);
  const ipTurnstileRef = useRef(null);
  const resultsContainerRef = useRef(null);

  const translations = {
    tr: {
      title: 'Ağ Araçları',
      description: 'Threat.live node\'larından Ping ve MTR testleri çalıştırın',
      selectNode: 'Node Seç',
      allNodes: 'Tüm Node\'lar',
      targetHost: 'Hedef IP / Hostname',
      targetPlaceholder: 'örn: 8.8.8.8 veya google.com',
      run: 'Çalıştır',
      running: 'Çalışıyor...',
      clear: 'Temizle',
      copy: 'Kopyala',
      copied: 'Kopyalandı!',
      copyImage: 'Görsel',
      downloadImage: 'İndir',
      ping: 'Ping',
      mtr: 'MTR',
      pingDesc: 'Hedefe ICMP ping gönder',
      mtrDesc: 'Ağ yolu analizi (traceroute + ping)',
      outputTitle: 'Çıktı',
      noOutput: 'Henüz çıktı yok. Bir test çalıştırın.',
      validationError: 'Lütfen geçerli bir hedef IP veya hostname girin',
      connectionError: 'Bağlantı hatası oluştu',
      turnstileError: 'Lütfen robot olmadığınızı doğrulayın',
      executionTime: 'Süre',
      nodeLabel: 'Node',
      success: 'Başarılı',
      error: 'Hata',
      totalNodes: 'Toplam Node',
      target: 'Hedef',
      completed: 'tamamlandı',
      apiError: 'API Hatası',
      compare: 'Karşılaştır',
      comparisonTitle: 'Node Karşılaştırması',
      avgLatency: 'Ort. Gecikme',
      minLatency: 'Min',
      maxLatency: 'Max',
      packetLoss: 'Kayıp',
      hops: 'Hop Sayısı',
      bestNode: 'En İyi',
      worstNode: 'En Kötü',
      noData: 'Veri yok',
      close: 'Kapat',
      // IP Info
      ipInfo: 'IP Bilgisi',
      ipInfoDesc: 'IP adreslerinin ASN, ülke ve Threat.live durumunu sorgula',
      ipAddresses: 'IP Adresleri',
      ipPlaceholder: 'IP adreslerini girin (her satıra bir IP veya virgülle ayırın)\nÖrnek: 1.1.1.1\n8.8.8.8, 8.8.4.4',
      search: 'Sorgula',
      searching: 'Sorgulanıyor...',
      resultsTitle: 'Sonuçlar',
      ip: 'IP Adresi',
      asn: 'ASN',
      asnOrg: 'Organizasyon',
      country: 'Ülke',
      ptr: 'rDNS',
      threatStatus: 'Threat.live Durumu',
      listed: 'Listelenmiş',
      notListed: 'Listelenmemiş',
      invalidIp: 'Lütfen geçerli IP adresi girin',
      maxIps: 'Maksimum 50 IP sorgulanabilir',
      noPtr: 'Kayıt yok',
      totalResults: 'Toplam Sonuç',
      listedCount: 'Listelenmiş',
      ipNote: 'Not: Her istekte maksimum 50 IP sorgulanabilir.',
      noIpResults: 'Henüz sorgu yapılmadı. IP adresi girin ve sorgulayın.',
      copyAll: 'Tümünü Kopyala',
      copyJson: 'JSON Kopyala',
      copyCsv: 'CSV Kopyala',
      // DNS Recursive Check
      dnsCheck: 'DNS Recursive',
      dnsCheckDesc: 'IP adreslerinin open recursive DNS açığı olup olmadığını kontrol et',
      dnsPlaceholder: 'IP adreslerini girin (her satıra bir IP veya virgülle ayırın, max 5)\nÖrnek: 8.8.8.8\n1.1.1.1',
      checkDns: 'Kontrol Et',
      checking: 'Kontrol ediliyor...',
      safe: 'Güvenli',
      vulnerable: 'Açık Var',
      noRecursion: 'Recursive kapalı',
      openRecursive: 'Open recursive DNS tespit edildi',
      responseIp: 'Yanıt IP',
      maxDnsIps: 'Maksimum 5 IP kontrol edilebilir',
      totalChecked: 'Kontrol Edilen',
      safeCount: 'Güvenli',
      vulnerableCount: 'Açık Var',
      dnsNoResults: 'Henüz sonuç yok. IP adresi girin ve kontrol edin.',
      dnsTarget: 'Hedef IP',
      dnsError: 'Hata',
    },
    en: {
      title: 'Network Tools',
      description: 'Run Ping and MTR tests from Threat.live nodes',
      selectNode: 'Select Node',
      allNodes: 'All Nodes',
      targetHost: 'Target IP / Hostname',
      targetPlaceholder: 'e.g., 8.8.8.8 or google.com',
      run: 'Run',
      running: 'Running...',
      clear: 'Clear',
      copy: 'Copy',
      copied: 'Copied!',
      copyImage: 'Image',
      downloadImage: 'Download',
      ping: 'Ping',
      mtr: 'MTR',
      pingDesc: 'Send ICMP ping to target',
      mtrDesc: 'Network path analysis (traceroute + ping)',
      outputTitle: 'Output',
      noOutput: 'No output yet. Run a test.',
      validationError: 'Please enter a valid target IP or hostname',
      connectionError: 'Connection error occurred',
      turnstileError: 'Please verify you are not a robot',
      executionTime: 'Duration',
      nodeLabel: 'Node',
      success: 'Success',
      error: 'Error',
      totalNodes: 'Total Nodes',
      target: 'Target',
      completed: 'completed',
      apiError: 'API Error',
      compare: 'Compare',
      comparisonTitle: 'Node Comparison',
      avgLatency: 'Avg Latency',
      minLatency: 'Min',
      maxLatency: 'Max',
      packetLoss: 'Loss',
      hops: 'Hops',
      bestNode: 'Best',
      worstNode: 'Worst',
      noData: 'No data',
      close: 'Close',
      // IP Info
      ipInfo: 'IP Info',
      ipInfoDesc: 'Query ASN, country and Threat.live status of IP addresses',
      ipAddresses: 'IP Addresses',
      ipPlaceholder: 'Enter IP addresses (one per line or comma-separated)\nExample: 1.1.1.1\n8.8.8.8, 8.8.4.4',
      search: 'Search',
      searching: 'Searching...',
      resultsTitle: 'Results',
      ip: 'IP Address',
      asn: 'ASN',
      asnOrg: 'Organization',
      country: 'Country',
      ptr: 'rDNS',
      threatStatus: 'Threat.live Status',
      listed: 'Listed',
      notListed: 'Not Listed',
      invalidIp: 'Please enter valid IP addresses',
      maxIps: 'Maximum 50 IPs can be queried',
      noPtr: 'No record',
      totalResults: 'Total Results',
      listedCount: 'Listed',
      ipNote: 'Note: Maximum 50 IPs can be queried per request.',
      noIpResults: 'No query yet. Enter IP addresses and search.',
      copyAll: 'Copy All',
      copyJson: 'Copy JSON',
      copyCsv: 'Copy CSV',
      // DNS Recursive Check
      dnsCheck: 'DNS Recursive',
      dnsCheckDesc: 'Check if IP addresses have open recursive DNS vulnerability',
      dnsPlaceholder: 'Enter IP addresses (one per line or comma-separated, max 5)\nExample: 8.8.8.8\n1.1.1.1',
      checkDns: 'Check',
      checking: 'Checking...',
      safe: 'Safe',
      vulnerable: 'Vulnerable',
      noRecursion: 'No recursion allowed',
      openRecursive: 'Open recursive DNS detected',
      responseIp: 'Response IP',
      maxDnsIps: 'Maximum 5 IPs can be checked',
      totalChecked: 'Total Checked',
      safeCount: 'Safe',
      vulnerableCount: 'Vulnerable',
      dnsNoResults: 'No results yet. Enter an IP address and check.',
      dnsTarget: 'Target IP',
      dnsError: 'Error',
    }
  };

  const txt = translations[language] || translations.en;

  // Parse ping output to extract metrics
  const parsePingOutput = (lines) => {
    const metrics = {
      avgLatency: null,
      minLatency: null,
      maxLatency: null,
      packetLoss: null,
      packetsTransmitted: null,
      packetsReceived: null,
    };

    const fullOutput = lines.join('\n');
    
    // Parse packet loss: "5 packets transmitted, 5 received, 0% packet loss"
    const lossMatch = fullOutput.match(/(\d+)%\s*packet\s*loss/i);
    if (lossMatch) {
      metrics.packetLoss = parseInt(lossMatch[1]);
    }

    // Parse packets transmitted/received
    const packetsMatch = fullOutput.match(/(\d+)\s*packets?\s*transmitted,\s*(\d+)\s*received/i);
    if (packetsMatch) {
      metrics.packetsTransmitted = parseInt(packetsMatch[1]);
      metrics.packetsReceived = parseInt(packetsMatch[2]);
    }

    // Parse rtt: "rtt min/avg/max/mdev = 11.234/12.456/14.567/1.234 ms"
    const rttMatch = fullOutput.match(/rtt\s+min\/avg\/max\/\S+\s*=\s*([\d.]+)\/([\d.]+)\/([\d.]+)/i);
    if (rttMatch) {
      metrics.minLatency = parseFloat(rttMatch[1]);
      metrics.avgLatency = parseFloat(rttMatch[2]);
      metrics.maxLatency = parseFloat(rttMatch[3]);
    }

    // Alternative format: "round-trip min/avg/max/stddev = ..."
    if (!rttMatch) {
      const altMatch = fullOutput.match(/round-trip\s+min\/avg\/max\/\S+\s*=\s*([\d.]+)\/([\d.]+)\/([\d.]+)/i);
      if (altMatch) {
        metrics.minLatency = parseFloat(altMatch[1]);
        metrics.avgLatency = parseFloat(altMatch[2]);
        metrics.maxLatency = parseFloat(altMatch[3]);
      }
    }

    return metrics;
  };

  // Parse MTR output to extract metrics
  const parseMtrOutput = (lines) => {
    const metrics = {
      avgLatency: null,
      minLatency: null,
      maxLatency: null,
      packetLoss: null,
      hops: 0,
    };

    const fullOutput = lines.join('\n');
    
    // MTR typically shows each hop with: Host, Loss%, Snt, Last, Avg, Best, Wrst, StDev
    // Count hops (lines with IP addresses or hostnames that have metrics)
    const hopLines = lines.filter(line => {
      return /^\s*\d+[\.\|]/.test(line) || /^\s*\d+\s+\S+\s+[\d.]+%/.test(line);
    });
    metrics.hops = hopLines.length;

    // Get the last hop's metrics (destination)
    if (hopLines.length > 0) {
      const lastHop = hopLines[hopLines.length - 1];
      
      // Try to parse: "10. target.com  0.0%  10  12.3  15.2  10.1  25.3  4.2"
      const mtrMatch = lastHop.match(/([\d.]+)%\s+\d+\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)/);
      if (mtrMatch) {
        metrics.packetLoss = parseFloat(mtrMatch[1]);
        metrics.avgLatency = parseFloat(mtrMatch[3]);
        metrics.minLatency = parseFloat(mtrMatch[4]);
        metrics.maxLatency = parseFloat(mtrMatch[5]);
      }
    }

    return metrics;
  };

  // Get comparison data from nodeResults
  const getComparisonData = () => {
    return nodeResults
      .filter(result => result.status === 'success')
      .map(result => {
        const metrics = activeTool === 'ping' 
          ? parsePingOutput(result.lines)
          : parseMtrOutput(result.lines);
        
        return {
          nodeLocation: result.nodeLocation,
          executionTime: result.executionTime,
          ...metrics
        };
      })
      .sort((a, b) => {
        // Sort by avg latency (best first)
        if (a.avgLatency === null) return 1;
        if (b.avgLatency === null) return -1;
        return a.avgLatency - b.avgLatency;
      });
  };

  // ==================== IP Info Functions ====================
  const parseIPs = (input) => {
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return input
      .split(/[\n,]+/)
      .map(ip => ip.trim())
      .filter(ip => ip && ipRegex.test(ip));
  };

  const handleIpSearch = async () => {
    setIpError('');
    const ips = parseIPs(ipInput);
    
    if (ips.length === 0) {
      setIpError(txt.invalidIp);
      return;
    }

    if (ips.length > 50) {
      setIpError(txt.maxIps);
      return;
    }

    if (!ipTurnstileToken) {
      setIpError(txt.turnstileError);
      return;
    }

    setIsRunning(true);
    setIpResults([]);

    try {
      const response = await fetch(IP_INFO_API, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          ips,
          'cf-turnstile-response': ipTurnstileToken 
        }),
      });

      const data = await response.json();

      if (data.status === 'success') {
        setIpResults(data.data || []);
      } else {
        setIpError(data.message || txt.apiError);
      }
    } catch (err) {
      console.error('IP Info error:', err);
      setIpError(txt.apiError);
    }

    // Reset turnstile after each request
    setIpTurnstileToken(null);
    if (window.turnstile && ipTurnstileRef.current) {
      window.turnstile.reset(ipTurnstileRef.current);
    }

    setIsRunning(false);
  };

  const clearIpResults = () => {
    setIpInput('');
    setIpResults([]);
    setIpError('');
  };

  // DNS Recursive Check Functions
  const handleDnsCheck = async () => {
    // Validate IP - only single IP for DNS check
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    const trimmedIp = dnsInput.trim();
    
    if (!trimmedIp || !ipRegex.test(trimmedIp)) {
      setDnsError(txt.invalidIp);
      return;
    }

    setDnsError('');
    setDnsLoading(true);
    setDnsResults(null);
    setDnsNodeResults([]);

    try {
      const response = await fetch(`${TOOLS_API_URL}.php?endpoint=dns-recursive-check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          ip: trimmedIp,
          node_id: selectedNode === 'all' ? 'all' : selectedNode
        }),
      });

      const data = await response.json();

      // Check for error status in response
      if (response.status === 400 || data.status === 'error') {
        setDnsError(data.message || data.error || txt.apiError);
      } else if (selectedNode === 'all' && data.results) {
        // Multiple nodes response
        setDnsNodeResults(data.results);
        setDnsResults({
          ip: data.ip,
          total_nodes: data.total_nodes
        });
      } else {
        // Single node response
        setDnsResults(data);
      }
    } catch (err) {
      console.error('DNS check error:', err);
      setDnsError(txt.apiError);
    }

    setDnsLoading(false);
  };

  const clearDnsResults = () => {
    setDnsInput('');
    setDnsResults(null);
    setDnsNodeResults([]);
    setDnsError('');
  };

  const getCountryFlag = (isoCode) => {
    if (!isoCode) return '🌐';
    const codePoints = isoCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  };

  const copyAllAsJson = () => {
    const json = JSON.stringify(ipResults, null, 2);
    navigator.clipboard.writeText(json);
    setCopiedAll('json');
    setTimeout(() => setCopiedAll(null), 2000);
  };

  const copyAllAsCsv = () => {
    const headers = ['IP', 'ASN', 'Organization', 'Country', 'ISO', 'rDNS', 'Threat.live Listed'];
    const rows = ipResults.map(r => [
      r.ip,
      r.asn || '',
      r.asn_org || '',
      r.country || '',
      r.iso_code || '',
      r.ptr || '',
      r.ip_listed ? 'Yes' : 'No'
    ]);
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    navigator.clipboard.writeText(csv);
    setCopiedAll('csv');
    setTimeout(() => setCopiedAll(null), 2000);
  };

  const copyAllAsText = () => {
    const text = ipResults.map(r => 
      `${r.ip} | AS${r.asn || '-'} ${r.asn_org || '-'} | ${r.country || '-'} | rDNS: ${r.ptr || '-'} | Threat.live: ${r.ip_listed ? 'Listed' : 'Not Listed'}`
    ).join('\n');
    navigator.clipboard.writeText(text);
    setCopiedAll('text');
    setTimeout(() => setCopiedAll(null), 2000);
  };

  const ipListedCount = ipResults.filter(r => r.ip_listed).length;

  // Fetch nodes on mount
  useEffect(() => {
    const fetchNodes = async () => {
      if (IS_PRERENDER) return;
      try {
        const response = await fetch('https://api.threat.live/get_nodes');
        const data = await response.json();
        setNodes(data);
      } catch (err) {
        console.error('Error fetching nodes:', err);
      }
    };
    fetchNodes();
  }, []);

  // Initialize Turnstile for Ping/MTR
  useEffect(() => {
    const initTurnstile = () => {
      if (window.turnstile && turnstileRef.current && !turnstileRef.current.hasChildNodes()) {
        window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITEKEY,
          callback: (token) => {
            setTurnstileToken(token);
          },
          'expired-callback': () => {
            setTurnstileToken(null);
          },
          'error-callback': () => {
            setTurnstileToken(null);
          },
          theme: 'dark',
        });
      }
    };

    // Wait for turnstile script to load
    if (window.turnstile) {
      initTurnstile();
    } else {
      const checkTurnstile = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkTurnstile);
          initTurnstile();
        }
      }, 100);
      
      // Cleanup interval after 10 seconds
      setTimeout(() => clearInterval(checkTurnstile), 10000);
    }
  }, []);

  // Initialize Turnstile for IP Info
  useEffect(() => {
    const initIpTurnstile = () => {
      if (window.turnstile && ipTurnstileRef.current && !ipTurnstileRef.current.hasChildNodes()) {
        window.turnstile.render(ipTurnstileRef.current, {
          sitekey: TURNSTILE_SITEKEY,
          callback: (token) => {
            setIpTurnstileToken(token);
          },
          'expired-callback': () => {
            setIpTurnstileToken(null);
          },
          'error-callback': () => {
            setIpTurnstileToken(null);
          },
          theme: 'dark',
        });
      }
    };

    // Wait for turnstile script to load
    if (window.turnstile) {
      initIpTurnstile();
    } else {
      const checkTurnstile = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkTurnstile);
          initIpTurnstile();
        }
      }, 100);
      
      // Cleanup interval after 10 seconds
      setTimeout(() => clearInterval(checkTurnstile), 10000);
    }
  }, [activeTool]);

  // Auto-scroll terminal
  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [output]);

  const addOutput = (text, type = 'info') => {
    setOutput(prev => [...prev, { text, type }]);
  };

  const validateTarget = (target) => {
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    const hostnameRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+$/;
    return ipRegex.test(target) || hostnameRegex.test(target);
  };

  const runTool = async () => {
    if (!target.trim()) {
      addOutput(`[!] ${txt.validationError}`, 'error');
      return;
    }

    if (!validateTarget(target.trim())) {
      addOutput(`[!] ${txt.validationError}`, 'error');
      return;
    }

    if (!turnstileToken) {
      addOutput(`[!] ${txt.turnstileError}`, 'error');
      return;
    }

    setIsRunning(true);
    setOutput([]); // Clear previous output
    setNodeResults([]); // Clear previous node results

    // Only show header in single terminal mode
    if (selectedNode !== 'all') {
      addOutput(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, 'separator');
      addOutput(`[${activeTool.toUpperCase()}] ${txt.target}: ${target}`, 'header');
      addOutput(`${txt.nodeLabel}: ${nodes.find(n => n.id.toString() === selectedNode)?.city || selectedNode}`, 'info');
      addOutput(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`, 'separator');
    }

    try {
      const response = await fetch(TOOLS_API_URL, {
        method: 'POST',
        headers: {
          'X-API-Key': TOOLS_API_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          command: activeTool,
          ip: target.trim(),
          node_id: selectedNode,
          'cf-turnstile-response': turnstileToken,
        }),
      });

      const data = await response.json();

      if (data.status === 'success' && data.results) {
        // Different handling for "all nodes" vs single node
        if (selectedNode === 'all') {
          // Card-based output for all nodes
          const processedResults = data.results.map(result => ({
            nodeId: result.node_id || result.node_location,
            nodeLocation: result.node_location,
            status: result.status,
            executionTime: result.execution_time_ms,
            message: result.message,
            lines: result.output ? result.output.split('\n').filter(line => line.trim()) : []
          }));
          setNodeResults(processedResults);
        } else {
          // Single terminal output for single node
          for (const result of data.results) {
            addOutput(`\n┌─────────────────────────────────────────────────────────`, 'separator');
            addOutput(`│ [${txt.nodeLabel.toUpperCase()}] ${result.node_location}`, 'header');
            addOutput(`│ [${txt.executionTime.toUpperCase()}] ${(result.execution_time_ms / 1000).toFixed(2)}s`, 'info');
            
            if (result.status === 'success') {
              addOutput(`│ [OK] ${txt.success}`, 'success');
              addOutput(`└─────────────────────────────────────────────────────────\n`, 'separator');
              
              if (result.output) {
                const lines = result.output.split('\n');
                lines.forEach(line => {
                  if (line.trim()) {
                    addOutput(line, 'output');
                  }
                });
              }
            } else {
              addOutput(`│ [FAIL] ${txt.error}: ${result.message || 'Unknown error'}`, 'error');
              addOutput(`└─────────────────────────────────────────────────────────\n`, 'separator');
            }
          }

          addOutput(`\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, 'separator');
          addOutput(`[+] ${activeTool.toUpperCase()} ${txt.completed}`, 'success');
          addOutput(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, 'separator');
        }

      } else {
        addOutput(`[!] ${txt.apiError}: ${data.message || 'Unknown error'}`, 'error');
      }

    } catch (err) {
      console.error('Tool error:', err);
      addOutput(`[!] ${txt.connectionError}: ${err.message}`, 'error');
    }

    // Reset turnstile after each request
    setTurnstileToken(null);
    if (window.turnstile && turnstileRef.current) {
      window.turnstile.reset(turnstileRef.current);
    }

    setIsRunning(false);
  };

  const clearOutput = () => {
    setOutput([]);
    setNodeResults([]);
  };

  const copyOutput = () => {
    let text;
    if (selectedNode === 'all' && nodeResults.length > 0) {
      // Copy all node results
      text = nodeResults.map(result => {
        const header = `=== ${result.nodeLocation} (${(result.executionTime / 1000).toFixed(2)}s) ===`;
        const content = result.lines.join('\n');
        return `${header}\n${content}`;
      }).join('\n\n');
    } else {
      text = output.map(o => o.text).join('\n');
    }
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyAsImage = async () => {
    const targetRef = selectedNode === 'all' && nodeResults.length > 0 ? resultsContainerRef.current : terminalRef.current;
    if (!targetRef) return;
    
    try {
      const canvas = await html2canvas(targetRef, {
        backgroundColor: '#1a1a2e',
        scale: 2,
        logging: false,
      });
      
      canvas.toBlob(async (blob) => {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({ 'image/png': blob })
          ]);
          setCopiedImage(true);
          setTimeout(() => setCopiedImage(false), 2000);
        } catch (err) {
          // Fallback: download the image if clipboard fails
          downloadImage(canvas);
        }
      }, 'image/png');
    } catch (err) {
      console.error('Error capturing image:', err);
    }
  };

  const downloadImage = async (existingCanvas = null) => {
    const targetRef = selectedNode === 'all' && nodeResults.length > 0 ? resultsContainerRef.current : terminalRef.current;
    if (!targetRef) return;
    
    try {
      const canvas = existingCanvas || await html2canvas(targetRef, {
        backgroundColor: '#1a1a2e',
        scale: 2,
        logging: false,
      });
      
      const link = document.createElement('a');
      link.download = `threat-live-${activeTool}-${target || 'output'}-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Error downloading image:', err);
    }
  };

  const getOutputColor = (type) => {
    switch (type) {
      case 'error': return 'text-red-400';
      case 'success': return 'text-green-400';
      case 'header': return 'text-cyan-400 font-bold';
      case 'separator': return 'text-gray-600';
      case 'info': return 'text-yellow-400';
      default: return 'text-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="tools-page">
      <SEO page="tools" />
      <Header />
      
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight mb-2 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-600">
              <Terminal className="h-6 w-6 text-white" />
            </div>
            {txt.title}
          </h1>
          <p className="text-muted-foreground">{txt.description}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Controls Panel */}
          <div className="lg:col-span-1 space-y-6">
            {/* Tool Selection */}
            <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Tool</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex justify-center p-2 bg-muted rounded-lg">
                  {/* Only IP Info tool visible */}
                  <Button
                    variant="ghost"
                    className="flex items-center justify-center gap-2 py-2.5 h-auto text-sm font-medium bg-background text-foreground shadow-sm"
                    data-testid="tool-ipinfo-btn"
                  >
                    <Info className="h-4 w-4 flex-shrink-0" />
                    <span>{txt.ipInfo}</span>
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  {txt.ipInfoDesc}
                </p>
              </CardContent>
            </Card>

            {/* Ping/MTR Controls */}
            {(activeTool === 'ping' || activeTool === 'mtr') && (
              <>
                {/* Node Selection */}
                <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Server className="h-4 w-4 text-emerald-500" />
                      {txt.selectNode}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Select value={selectedNode} onValueChange={setSelectedNode}>
                      <SelectTrigger data-testid="node-select">
                        <SelectValue placeholder={txt.selectNode} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">
                          <div className="flex items-center gap-2">
                            <Globe className="h-4 w-4" />
                            {txt.allNodes} ({nodes.length})
                          </div>
                        </SelectItem>
                        {nodes.map(node => (
                          <SelectItem key={node.id} value={node.id.toString()}>
                            <div className="flex items-center gap-2">
                              <span>{node.city}, {node.country}</span>
                              <Badge variant="outline" className="text-xs">
                                {node.datacenter}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </CardContent>
                </Card>

                {/* Target Input */}
            <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">{txt.targetHost}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  placeholder={txt.targetPlaceholder}
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  className="font-mono"
                  data-testid="target-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isRunning && target.trim() && turnstileToken) {
                      runTool();
                    }
                  }}
                />
                
                {/* Cloudflare Turnstile */}
                <div 
                  ref={turnstileRef} 
                  className="flex justify-center"
                  data-testid="turnstile-widget"
                ></div>
                
                <Button 
                  onClick={runTool} 
                  disabled={isRunning || !target.trim() || !turnstileToken}
                  className="w-full bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700"
                  data-testid="run-button"
                >
                  {isRunning ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {txt.running}
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      {txt.run}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
              </>
            )}

            {/* IP Info Controls */}
            {activeTool === 'ipinfo' && (
              <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Search className="h-4 w-4 text-emerald-500" />
                    {txt.ipAddresses}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Textarea
                    placeholder={txt.ipPlaceholder}
                    value={ipInput}
                    onChange={(e) => setIpInput(e.target.value)}
                    className="min-h-[180px] font-mono text-sm"
                    data-testid="ip-input"
                  />
                  
                  <p className="text-xs text-muted-foreground">
                    {txt.ipNote}
                  </p>

                  {ipError && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                      <p className="text-sm text-red-500">{ipError}</p>
                    </div>
                  )}

                  {/* Cloudflare Turnstile for IP Info */}
                  <div 
                    ref={ipTurnstileRef} 
                    className="flex justify-center"
                    data-testid="ip-turnstile-widget"
                  ></div>
                  
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleIpSearch} 
                      disabled={isRunning || !ipInput.trim() || !ipTurnstileToken}
                      className="flex-1 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700"
                      data-testid="ip-search-button"
                    >
                      {isRunning ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {txt.searching}
                        </>
                      ) : (
                        <>
                          <Search className="h-4 w-4 mr-2" />
                          {txt.search}
                        </>
                      )}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={clearIpResults}
                      disabled={isRunning || (!ipInput && ipResults.length === 0)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* DNS Recursive Check Controls */}
            {activeTool === 'dnscheck' && (
              <>
                {/* Node Selection for DNS */}
                <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Server className="h-4 w-4 text-emerald-500" />
                      {txt.selectNode}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Select value={selectedNode} onValueChange={setSelectedNode}>
                      <SelectTrigger className="w-full" data-testid="dns-node-select">
                        <SelectValue placeholder={txt.selectNode} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">
                          <div className="flex items-center gap-2">
                            <Globe className="h-4 w-4" />
                            {txt.allNodes} ({nodes.length})
                          </div>
                        </SelectItem>
                        {nodes.map(node => (
                          <SelectItem key={node.id} value={node.id.toString()}>
                            <div className="flex items-center gap-2">
                              <span>{node.city}, {node.country}</span>
                              <Badge variant="outline" className="text-xs">
                                {node.datacenter}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </CardContent>
                </Card>

                {/* DNS Target IP Input */}
                <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Wifi className="h-4 w-4 text-emerald-500" />
                      {txt.dnsTarget}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Input
                      placeholder="örn: 8.8.8.8"
                      value={dnsInput}
                      onChange={(e) => setDnsInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !dnsLoading) {
                          handleDnsCheck();
                        }
                      }}
                      className="font-mono"
                      data-testid="dns-input"
                    />

                    {dnsError && (
                      <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                        <p className="text-sm text-red-500">{dnsError}</p>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <Button 
                        onClick={handleDnsCheck} 
                        disabled={dnsLoading || !dnsInput.trim()}
                        className="flex-1 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700"
                        data-testid="dns-check-button"
                      >
                        {dnsLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            {txt.checking}
                          </>
                        ) : (
                          <>
                            <Play className="h-4 w-4 mr-2" />
                            {txt.checkDns}
                          </>
                        )}
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={clearDnsResults}
                        disabled={dnsLoading || (!dnsInput && !dnsResults && dnsNodeResults.length === 0)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {/* Ping/MTR Terminal Output */}
          {(activeTool === 'ping' || activeTool === 'mtr') && (
            <div className="lg:col-span-2">
            <Card className="border-0 bg-gradient-to-br from-card to-muted/30 h-full">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Terminal className="h-4 w-4 text-emerald-500" />
                    {txt.outputTitle}
                    {selectedNode === 'all' && nodeResults.length > 0 && (
                      <Badge variant="outline" className="ml-2">
                        {nodeResults.length} {txt.totalNodes.toLowerCase()}
                      </Badge>
                    )}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={copyOutput}
                      disabled={output.length === 0 && nodeResults.length === 0}
                      title={txt.copy}
                    >
                      {copied ? (
                        <><Check className="h-4 w-4 mr-1" />{txt.copied}</>
                      ) : (
                        <><Copy className="h-4 w-4 mr-1" />{txt.copy}</>
                      )}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={copyAsImage}
                      disabled={output.length === 0 && nodeResults.length === 0}
                      title={txt.copyImage}
                    >
                      {copiedImage ? (
                        <><Check className="h-4 w-4 mr-1" />{txt.copied}</>
                      ) : (
                        <><Image className="h-4 w-4 mr-1" />{txt.copyImage}</>
                      )}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => downloadImage()}
                      disabled={output.length === 0 && nodeResults.length === 0}
                      title={txt.downloadImage}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      {txt.downloadImage}
                    </Button>
                    {/* Compare Button - only visible when multiple node results */}
                    {nodeResults.length > 1 && (
                      <Button 
                        variant="default" 
                        size="sm" 
                        onClick={() => setShowComparison(true)}
                        className="bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700"
                        data-testid="compare-button"
                      >
                        <BarChart3 className="h-4 w-4 mr-1" />
                        {txt.compare}
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={clearOutput}
                      disabled={output.length === 0 && nodeResults.length === 0}
                      title={txt.clear}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      {txt.clear}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Card-based output for All Nodes */}
                {selectedNode === 'all' && nodeResults.length > 0 ? (
                  <div 
                    ref={resultsContainerRef}
                    className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[600px] overflow-y-auto p-2"
                    data-testid="node-results-grid"
                  >
                    {nodeResults.map((result, index) => (
                      <Card 
                        key={result.nodeId || index} 
                        className={`border ${result.status === 'success' ? 'border-emerald-500/30' : 'border-red-500/30'} bg-[#1a1a2e]`}
                        data-testid={`node-result-card-${index}`}
                      >
                        <CardHeader className="py-3 px-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Server className="h-4 w-4 text-emerald-500" />
                              <span className="font-semibold text-sm text-white">{result.nodeLocation}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${result.status === 'success' ? 'border-emerald-500/50 text-emerald-400' : 'border-red-500/50 text-red-400'}`}
                              >
                                {result.status === 'success' ? txt.success : txt.error}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {(result.executionTime / 1000).toFixed(2)}s
                              </span>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="py-2 px-4">
                          <div className="bg-[#0d0d1a] rounded p-3 max-h-[200px] overflow-y-auto font-mono text-xs">
                            {result.status === 'success' && result.lines.length > 0 ? (
                              result.lines.map((line, lineIndex) => (
                                <div key={lineIndex} className="text-gray-300 whitespace-pre-wrap">
                                  {line}
                                </div>
                              ))
                            ) : result.status !== 'success' ? (
                              <div className="text-red-400">{result.message || 'Unknown error'}</div>
                            ) : (
                              <div className="text-gray-500">No output</div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  /* Single terminal output for single node */
                  <div 
                    ref={terminalRef}
                    className="bg-[#1a1a2e] rounded-lg p-4 h-[500px] overflow-y-auto font-mono text-sm"
                    data-testid="terminal-output"
                  >
                    {output.length === 0 && nodeResults.length === 0 ? (
                      <div className="text-gray-500 flex items-center justify-center h-full">
                        <div className="text-center">
                          <Terminal className="h-12 w-12 mx-auto mb-3 opacity-30" />
                          <p>{txt.noOutput}</p>
                        </div>
                      </div>
                    ) : (
                      output.map((line, index) => (
                        <div key={index} className={`${getOutputColor(line.type)} whitespace-pre-wrap`}>
                          {line.text}
                        </div>
                      ))
                    )}
                    {isRunning && (
                      <div className="flex items-center gap-2 text-emerald-400 mt-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="animate-pulse">Processing...</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Loading indicator for card view */}
                {isRunning && selectedNode === 'all' && nodeResults.length === 0 && (
                  <div className="flex items-center justify-center h-[300px]">
                    <div className="text-center">
                      <Loader2 className="h-12 w-12 mx-auto mb-3 animate-spin text-emerald-500" />
                      <p className="text-muted-foreground">{txt.running}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          )}

          {/* IP Info Results */}
          {activeTool === 'ipinfo' && (
            <div className="lg:col-span-2">
              <Card className="border-0 bg-gradient-to-br from-card to-muted/30 h-full">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Globe className="h-4 w-4 text-emerald-500" />
                      {txt.resultsTitle}
                    </CardTitle>
                    {ipResults.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">
                          {txt.totalResults}: {ipResults.length}
                        </Badge>
                        {ipListedCount > 0 && (
                          <Badge variant="destructive" className="bg-red-500">
                            <ShieldAlert className="h-3 w-3 mr-1" />
                            {txt.listedCount}: {ipListedCount}
                          </Badge>
                        )}
                        {/* Copy All Buttons */}
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={copyAllAsText}
                          title={txt.copyAll}
                        >
                          {copiedAll === 'text' ? (
                            <><Check className="h-4 w-4 mr-1" />{txt.copied}</>
                          ) : (
                            <><Copy className="h-4 w-4 mr-1" />{txt.copyAll}</>
                          )}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={copyAllAsJson}
                          title={txt.copyJson}
                        >
                          {copiedAll === 'json' ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <FileJson className="h-4 w-4" />
                          )}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={copyAllAsCsv}
                          title={txt.copyCsv}
                        >
                          {copiedAll === 'csv' ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <FileSpreadsheet className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {ipResults.length === 0 && !isRunning ? (
                    <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                      <div className="text-center">
                        <Search className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p>{txt.noIpResults}</p>
                      </div>
                    </div>
                  ) : isRunning ? (
                    <div className="flex items-center justify-center h-[300px]">
                      <div className="text-center">
                        <Loader2 className="h-12 w-12 mx-auto mb-3 animate-spin text-emerald-500" />
                        <p className="text-muted-foreground">{txt.searching}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="overflow-x-auto max-h-[500px]">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{txt.ip}</TableHead>
                            <TableHead>{txt.asn}</TableHead>
                            <TableHead>{txt.asnOrg}</TableHead>
                            <TableHead>{txt.country}</TableHead>
                            <TableHead>{txt.ptr}</TableHead>
                            <TableHead>{txt.threatStatus}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {ipResults.map((result, index) => (
                            <TableRow key={index} className={result.ip_listed ? 'bg-red-500/5' : ''}>
                              <TableCell>
                                <span className="font-mono font-medium">{result.ip}</span>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Server className="h-3 w-3 text-muted-foreground" />
                                  <span className="font-mono">AS{result.asn || '-'}</span>
                                  {result.asn && (
                                    <a 
                                      href={`https://bgp.tools/as/${result.asn}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-muted-foreground hover:text-emerald-500 transition-colors"
                                      title="bgp.tools"
                                    >
                                      <ExternalLink className="h-3 w-3" />
                                    </a>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm max-w-[200px] truncate block" title={result.asn_org}>
                                  {result.asn_org || '-'}
                                </span>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <span className="text-lg">{getCountryFlag(result.iso_code)}</span>
                                  <span className="text-sm">{result.country || '-'}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className="font-mono text-xs text-muted-foreground max-w-[150px] truncate block" title={result.ptr}>
                                  {result.ptr || txt.noPtr}
                                </span>
                              </TableCell>
                              <TableCell>
                                {result.ip_listed ? (
                                  <Badge variant="destructive" className="bg-red-500">
                                    <ShieldAlert className="h-3 w-3 mr-1" />
                                    {txt.listed}
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="border-emerald-500/50 text-emerald-500">
                                    <ShieldCheck className="h-3 w-3 mr-1" />
                                    {txt.notListed}
                                  </Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* DNS Recursive Check Results */}
          {activeTool === 'dnscheck' && (
            <div className="lg:col-span-2">
              <Card className="border-0 bg-gradient-to-br from-card to-muted/30 h-full">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Wifi className="h-4 w-4 text-emerald-500" />
                      {txt.resultsTitle}
                      {selectedNode === 'all' && dnsNodeResults.length > 0 && (
                        <Badge variant="outline" className="ml-2">
                          {dnsNodeResults.length} {txt.totalNodes.toLowerCase()}
                        </Badge>
                      )}
                    </CardTitle>
                    {(dnsResults || dnsNodeResults.length > 0) && (
                      <div className="flex items-center gap-2">
                        {/* Count safe/vulnerable/error */}
                        {dnsNodeResults.length > 0 && (
                          <>
                            <Badge variant="outline" className="border-emerald-500/50 text-emerald-500">
                              <ShieldCheck className="h-3 w-3 mr-1" />
                              {txt.safeCount}: {dnsNodeResults.filter(r => r.status === 'safe').length}
                            </Badge>
                            <Badge variant="destructive" className="bg-red-500">
                              <ShieldAlert className="h-3 w-3 mr-1" />
                              {txt.vulnerableCount}: {dnsNodeResults.filter(r => r.status === 'vulnerable').length}
                            </Badge>
                            {dnsNodeResults.filter(r => r.status === 'error').length > 0 && (
                              <Badge className="bg-orange-500">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                {txt.dnsError}: {dnsNodeResults.filter(r => r.status === 'error').length}
                              </Badge>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* No results yet */}
                  {!dnsResults && dnsNodeResults.length === 0 && !dnsLoading ? (
                    <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                      <div className="text-center">
                        <Wifi className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p>{txt.dnsNoResults}</p>
                      </div>
                    </div>
                  ) : dnsLoading ? (
                    <div className="flex items-center justify-center h-[300px]">
                      <div className="text-center">
                        <Loader2 className="h-12 w-12 mx-auto mb-3 animate-spin text-emerald-500" />
                        <p className="text-muted-foreground">{txt.checking}</p>
                      </div>
                    </div>
                  ) : selectedNode !== 'all' && dnsResults ? (
                    /* Single Node Result */
                    <div className="flex items-center justify-center h-[300px]">
                      <div className="text-center space-y-4">
                        {dnsResults.status === 'safe' ? (
                          <>
                            <div className="w-24 h-24 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center">
                              <ShieldCheck className="h-12 w-12 text-emerald-500" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-emerald-500">{txt.safe}</p>
                              <p className="text-muted-foreground">{txt.noRecursion}</p>
                            </div>
                          </>
                        ) : dnsResults.status === 'error' ? (
                          <>
                            <div className="w-24 h-24 mx-auto rounded-full bg-orange-500/20 flex items-center justify-center">
                              <AlertTriangle className="h-12 w-12 text-orange-500" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-orange-500">{txt.dnsError}</p>
                              <p className="text-muted-foreground">{dnsResults.message || txt.apiError}</p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="w-24 h-24 mx-auto rounded-full bg-red-500/20 flex items-center justify-center">
                              <ShieldAlert className="h-12 w-12 text-red-500" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-red-500">{txt.vulnerable}</p>
                              <p className="text-muted-foreground">{txt.openRecursive}</p>
                              {dnsResults.response && (
                                <p className="text-sm font-mono mt-2 text-red-400">
                                  {txt.responseIp}: {dnsResults.response}
                                </p>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ) : dnsNodeResults.length > 0 ? (
                    /* Multiple Nodes Results - Cards Layout */
                    <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                      {dnsNodeResults.map((result, index) => {
                        const isError = result.status === 'error';
                        const isSafe = result.status === 'safe';
                        const isVulnerable = result.status === 'vulnerable';
                        
                        return (
                          <Card 
                            key={index} 
                            className={`border ${
                              isSafe ? 'border-emerald-500/30 bg-emerald-500/5' : 
                              isError ? 'border-orange-500/30 bg-orange-500/5' :
                              'border-red-500/30 bg-red-500/5'
                            }`}
                            data-testid={`dns-result-${index}`}
                          >
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`p-2 rounded-lg ${
                                    isSafe ? 'bg-emerald-500/20' : 
                                    isError ? 'bg-orange-500/20' :
                                    'bg-red-500/20'
                                  }`}>
                                    {isSafe ? (
                                      <ShieldCheck className="h-5 w-5 text-emerald-500" />
                                    ) : isError ? (
                                      <AlertTriangle className="h-5 w-5 text-orange-500" />
                                    ) : (
                                      <ShieldAlert className="h-5 w-5 text-red-500" />
                                    )}
                                  </div>
                                  <div>
                                    <p className="font-medium">{result.node_location}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {result.execution_time_ms?.toFixed(2)} ms
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <Badge 
                                    variant={isSafe ? 'outline' : 'destructive'}
                                    className={
                                      isSafe ? 'border-emerald-500/50 text-emerald-500' : 
                                      isError ? 'bg-orange-500' :
                                      'bg-red-500'
                                    }
                                  >
                                    {isSafe ? txt.safe : isError ? txt.dnsError : txt.vulnerable}
                                  </Badge>
                                  {result.message && isError && (
                                    <p className="text-xs text-orange-400 mt-1">
                                      {result.message}
                                    </p>
                                  )}
                                  {result.response && !isError && (
                                    <p className="text-xs font-mono text-red-400 mt-1">
                                      {result.response}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      {/* Comparison Modal */}
      {showComparison && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="comparison-modal">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden border-0 bg-gradient-to-br from-card to-muted/50">
            <CardHeader className="pb-4 border-b border-border/50">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-600">
                    <BarChart3 className="h-5 w-5 text-white" />
                  </div>
                  {txt.comparisonTitle}
                  <Badge variant="outline" className="ml-2">
                    {activeTool.toUpperCase()} → {target}
                  </Badge>
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowComparison(false)}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
              {(() => {
                const comparisonData = getComparisonData();
                const bestNode = comparisonData[0];
                const worstNode = comparisonData[comparisonData.length - 1];
                
                return (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 gap-4">
                      {bestNode && bestNode.avgLatency !== null && (
                        <Card className="border border-emerald-500/30 bg-emerald-500/5">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-2 text-emerald-500 mb-2">
                              <TrendingUp className="h-4 w-4" />
                              <span className="text-sm font-medium">{txt.bestNode}</span>
                            </div>
                            <div className="text-lg font-bold">{bestNode.nodeLocation}</div>
                            <div className="text-2xl font-mono text-emerald-400">
                              {bestNode.avgLatency.toFixed(2)} ms
                            </div>
                          </CardContent>
                        </Card>
                      )}
                      {worstNode && worstNode.avgLatency !== null && comparisonData.length > 1 && (
                        <Card className="border border-orange-500/30 bg-orange-500/5">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-2 text-orange-500 mb-2">
                              <TrendingDown className="h-4 w-4" />
                              <span className="text-sm font-medium">{txt.worstNode}</span>
                            </div>
                            <div className="text-lg font-bold">{worstNode.nodeLocation}</div>
                            <div className="text-2xl font-mono text-orange-400">
                              {worstNode.avgLatency.toFixed(2)} ms
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>

                    {/* Comparison Table */}
                    <div className="rounded-lg border border-border/50 overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-3 font-medium">{txt.nodeLabel}</th>
                            <th className="text-center p-3 font-medium">{txt.avgLatency}</th>
                            <th className="text-center p-3 font-medium">{txt.minLatency}</th>
                            <th className="text-center p-3 font-medium">{txt.maxLatency}</th>
                            <th className="text-center p-3 font-medium">{txt.packetLoss}</th>
                            {activeTool === 'mtr' && (
                              <th className="text-center p-3 font-medium">{txt.hops}</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {comparisonData.map((data, index) => {
                            const isBest = index === 0 && data.avgLatency !== null;
                            const isWorst = index === comparisonData.length - 1 && comparisonData.length > 1 && data.avgLatency !== null;
                            
                            return (
                              <tr 
                                key={index} 
                                className={`border-t border-border/30 ${isBest ? 'bg-emerald-500/5' : isWorst ? 'bg-orange-500/5' : ''}`}
                              >
                                <td className="p-3">
                                  <div className="flex items-center gap-2">
                                    <Server className="h-4 w-4 text-muted-foreground" />
                                    <span className="font-medium">{data.nodeLocation}</span>
                                    {isBest && <Badge className="bg-emerald-500/20 text-emerald-500 text-xs">Best</Badge>}
                                    {isWorst && <Badge className="bg-orange-500/20 text-orange-500 text-xs">Slowest</Badge>}
                                  </div>
                                </td>
                                <td className="p-3 text-center font-mono">
                                  {data.avgLatency !== null ? (
                                    <span className={isBest ? 'text-emerald-400' : isWorst ? 'text-orange-400' : ''}>
                                      {data.avgLatency.toFixed(2)} ms
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground">{txt.noData}</span>
                                  )}
                                </td>
                                <td className="p-3 text-center font-mono text-muted-foreground">
                                  {data.minLatency !== null ? `${data.minLatency.toFixed(2)} ms` : '-'}
                                </td>
                                <td className="p-3 text-center font-mono text-muted-foreground">
                                  {data.maxLatency !== null ? `${data.maxLatency.toFixed(2)} ms` : '-'}
                                </td>
                                <td className="p-3 text-center">
                                  {data.packetLoss !== null ? (
                                    <Badge 
                                      variant="outline" 
                                      className={data.packetLoss === 0 
                                        ? 'border-emerald-500/50 text-emerald-400' 
                                        : data.packetLoss < 10 
                                          ? 'border-yellow-500/50 text-yellow-400'
                                          : 'border-red-500/50 text-red-400'
                                      }
                                    >
                                      {data.packetLoss}%
                                    </Badge>
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                </td>
                                {activeTool === 'mtr' && (
                                  <td className="p-3 text-center font-mono text-muted-foreground">
                                    {data.hops > 0 ? data.hops : '-'}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ToolsPage;
